import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  Component,
  effect,
  EventEmitter,
  inject,
  input,
  InputSignal,
  Output,
  output,
  OutputEmitterRef,
  signal,
  ViewChild,
  WritableSignal
} from '@angular/core';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import {
  MatCell,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderRow,
  MatNoDataRow,
  MatRow,
  MatTable,
  MatTableDataSource,
  MatTableModule
} from '@angular/material/table';
import { take } from 'rxjs';
import {
  PaginationResponse,
  ReportEntity,
  TableSetting,
  TableSettingRequest
} from '../../../../../../api/api.model';
import { ApiService } from '../../../../../../api/api.service';
import { SidenavService } from '../../../../../../partials/services/sidenav.service';
import { AuthService } from '../../../../../../partials/services/auth.service';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { DatePipe, DecimalPipe, NgClass, NgOptimizedImage, NgStyle, TitleCasePipe } from '@angular/common';
import { FormControl, FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatRipple } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { CopyButtonComponent } from '../../../../../../partials/components/copy-button/copy-button.component';
import { ReportsComponent } from '../../../../../../partials/components/reports/reports.component';
import { TransformUnderscoreToSpacePipe } from '../../../../../../partials/pipes/tranform-underscore-to-space.pipe';
import { MatSelectModule } from '@angular/material/select';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MonitoringFacade } from '../../../../monitoring.facade';
import {
  EmptyTableColumnWidthConfig,
  EmptyTableComponent
} from "@/app/partials/components/empty-table/empty-table.component";
import { EmptyPaginationComponent } from "@/app/partials/components/empty-pagination/empty-pagination.component";
import { ActivatedRoute, Router } from '@angular/router';

export interface ReportsTableSettings {
  allDisplayedColumns: string[];
  filteredDisplayedColumns: string[];
}

// Имя таблицы необходимо для сохранения настроек в API
export const tableName = 'ReportsTable';

@Component({
  selector: 'app-reports-table',
  imports: [
    MatIconModule,
    MatTable,
    MatColumnDef,
    MatHeaderCell,
    MatCell,
    MatHeaderRow,
    MatRow,
    MatFormFieldModule,
    MatRipple,
    MatPaginatorModule,
    MatNoDataRow,
    DatePipe,
    MatMenuTrigger,
    MatMenu,
    CdkDropList,
    CdkDrag,
    CdkScrollable,
    CdkDragHandle,
    MatCheckbox,
    FormsModule,
    MatTableModule,
    CopyButtonComponent,
    NgClass,
    TransformUnderscoreToSpacePipe,
    TitleCasePipe,
    MatSelectModule,
    ReactiveFormsModule,
    DecimalPipe,
    EmptyTableComponent,
    EmptyPaginationComponent,
    NgStyle
  ],
  templateUrl: './reports-table.component.html',
  styleUrl: './reports-table.component.scss'
})
export class ReportsTableComponent {
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  private readonly _monitoringFacade: MonitoringFacade = inject(MonitoringFacade);
  private readonly _route: ActivatedRoute = inject(ActivatedRoute);
  private readonly _router: Router = inject(Router);
  private readonly _apiService: ApiService = inject(ApiService);

  private _mainSidenavService: SidenavService = inject(SidenavService);

  private readonly _authService: AuthService = inject(AuthService);

  readonly pageEvent: OutputEmitterRef<PageEvent> = output<PageEvent>();

  readonly paginationResponseData: InputSignal<PaginationResponse<Omit<ReportEntity, 'data'>>> = input.required<PaginationResponse<Omit<ReportEntity, 'data'>>>();

  @Output() outputChangeNicknameFilter: EventEmitter<string | null> = new EventEmitter<string | null>();
  readonly paginationOptions: InputSignal<{
    pageSize: number;
    pageIndex: number;
    pageSizeOptions: number[];
    length: number
  }> = input.required<{ pageSize: number; pageIndex: number; pageSizeOptions: number[]; length: number }>();

  public markOpenRow: WritableSignal<number | null> = signal(null);
  private readonly _isSidenavOpen: WritableSignal<boolean> = signal(false);

  public readonly nicknamesList: WritableSignal<string[]> = this._monitoringFacade.reportsNicknames;
  private readonly _fb: NonNullableFormBuilder = inject(NonNullableFormBuilder);
  public filterNickname: FormControl<string | null> = this._fb.control(null);

  readonly tableSettings: InputSignal<TableSetting<ReportsTableSettings> | undefined> = input<TableSetting<ReportsTableSettings> | undefined>();

  allDisplayedColumns: string[] = [
    'report_id',
    'report_type',
    'date',
    'wallet',
    'risk_score',
    'nickname',
    'service_fee',
    'review',
    'table_settings'
  ];

  columnWidthResponsiveMap: Record<string, EmptyTableColumnWidthConfig> = {
    report_id: { base: 144 },
    report_type: { base: 266 },
    date: { base: 204 },
    wallet: { base: 573 },
    risk_score: { base: 131 },
    nickname: { base: 183 },
    service_fee: { base: 183 },
    review: { base: 160 },
    table_settings: { base: 10, visibility: 'hidden' }
  }

  filteredDisplayedColumns: string[] = [];

  get displayedColumns() {
    return this.allDisplayedColumns.filter((column) => !this.filteredDisplayedColumns.includes(column));
  };

  get displayedColumnsEmpty() {
    const displayedColumns = this.displayedColumns;
    return displayedColumns.filter(item => item );
  };

  readonly dataSource: MatTableDataSource<Omit<ReportEntity, 'data'>> = new MatTableDataSource<Omit<ReportEntity, 'data'>>([]);

  constructor() {
    this._monitoringFacade.ensureReportsNicknamesLoaded();

    this.filterNickname.valueChanges.pipe(takeUntilDestroyed()).subscribe({
      next: (value) => {
        this._monitoringFacade.loadReports({nickname: (value ?? ''), page: 0});
        // this.outputChangeNicknameFilter.emit(value);
      }
    });
    effect(() => {
      const { content } = this.paginationResponseData();
      this.dataSource.data = content;
    });

    effect(() => {
      const loadedTableSettings = this.tableSettings();

      if (loadedTableSettings) {
        const { data: { allDisplayedColumns, filteredDisplayedColumns } } = loadedTableSettings;
        // Проверка нужно для того чтоб если были изменения в allDisplayedColumns. Мы не перезаписывали сохраненными настройками.
        if (allDisplayedColumns.every((displayedColumn) => this.allDisplayedColumns.includes(displayedColumn))) {
          this.allDisplayedColumns = allDisplayedColumns;
          this.filteredDisplayedColumns = filteredDisplayedColumns;
        }
      }
    });

    effect(() => {
      const report = this._monitoringFacade.openedReport();
      const isOpen = this._isSidenavOpen();

      if (!report) return;

      const inputs = { inputReport: report };
      if (!isOpen) {
        this.markOpenRow.set(report.id);
        this._isSidenavOpen.set(true);
        this._mainSidenavService.open<boolean>({
          template: ReportsComponent,
          inputs
        }).afterClosed.pipe(take(1)).subscribe({
          next: () => {
            this._isSidenavOpen.set(false);
            this.markOpenRow.set(null);
            this._monitoringFacade.closeReport();
            this._router.navigate([], {
              relativeTo: this._route,
              queryParams: { reportId: null, process: null },
              queryParamsHandling: 'merge'
            });
          }
        });
      } else {
        this._mainSidenavService.updateInputs(inputs);
      }
    });
  }

  ngOnInit(): void {}

  private _saveTableSettings(): void {
    const user = this._authService.accountDto();

    if (!user) return;

    const { uuid } = user;

    const body: TableSettingRequest = {
      data: {
        allDisplayedColumns: this.allDisplayedColumns,
        filteredDisplayedColumns: this.filteredDisplayedColumns,
      },
      tableName,
      accountId: uuid
    };
    this._apiService.createOrUpdateTableSetting<ReportsTableSettings>(body).pipe(take(1)).subscribe({
      next: () => {
        console.log(`${ tableName } settings saved!`);
      }
    });
  }
  handlerChangeDisplayColumnCheckbox({ checked }: MatCheckboxChange, displayedColumn: string): void {
    if (checked) {
      this.filteredDisplayedColumns = this.filteredDisplayedColumns.filter((c) => c !== displayedColumn);
    } else {
      this.filteredDisplayedColumns.push(displayedColumn);
    }
    this._saveTableSettings();
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.allDisplayedColumns, event.previousIndex, event.currentIndex);
    this._saveTableSettings();
  }

  viewReport(report: Omit<ReportEntity, 'data'>): void {
    this.markOpenRow.set(report.id);
    this._monitoringFacade.openReport(report.id);
  }
}
